import { convertToISOStringInTimeZone } from '../../../datetime.js'

/** A `YYYY-MM-DD` day and nothing else. */
const DAY = /^\d{4}-\d{2}-\d{2}$/

/** A `YYYY-MM-DD` day carrying a time, e.g. `2026-08-27T10:00:00.000Z`. */
const INSTANT = /^\d{4}-\d{2}-\d{2}T/

/**
 * The day a date field names, in either of the two shapes it is read in.
 *
 * A document writes a day such as a planning's `start_date` as a bare date, and
 * the index hands the very same field back as midnight UTC - see
 * `__tests__/data/planning-index.json`. Both shapes therefore reach whoever
 * reads one, and both name the same day.
 *
 * A bare date is taken as written and never read as an instant: read as one it
 * is midnight *UTC*, which anywhere behind Greenwich is still the evening
 * before, landing the day before the one the field names.
 *
 * A date carrying a time is an instant, and an instant is a day only once a
 * timezone has been picked to read it in - the registry's own, the one the rest
 * of a memo's days are counted in, rather than whichever one the browser
 * happens to be set to.
 *
 * Anything naming no day at all is left to the caller's fallbacks. The shape is
 * checked as well as the parse, for the same reason the candidate query's
 * `parseDay` checks it: the parse alone reads `2026-09` as the first of that
 * month, and a day nobody chose is no better than one that does not parse.
 */
export function indexDay(value: string, timeZone: string): string | undefined {
  if (DAY.test(value)) {
    return value
  }

  if (!INSTANT.test(value)) {
    return undefined
  }

  const instant = new Date(value)

  if (isNaN(instant.getTime())) {
    return undefined
  }

  return convertToISOStringInTimeZone(instant, timeZone).slice(0, 10)
}
